import React, { useEffect, useState } from "react";
import SellerCard from "../components/SellerCard.js";
import { getFavouriteStreams } from "../services/stream.service.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSellerProfileUrl } from "../../../utils/commonfunction.js";

const PER_PAGE = 12;

function Favourites() {
  const { t } = useTranslation();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const fetchFavourites = async () => {
    try {
      setLoading(true);
      const res = await getFavouriteStreams();
      if (res?.success) {
        setSellers(res.data?.favouriteStreamers || []);
      }
    } catch (err) {
      console.error("Error fetching favorites:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavourites();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(sellers.length / PER_PAGE);
  const currentItems = sellers.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-2 mt-12 pb-8">
        <button
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-4 py-2 rounded-md border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-100 transition shadow-sm font-semibold"
        >
          {t("common.prev")}
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
            acc.push(p);
            return acc;
          }, [])
          .map((item, idx) =>
            item === "..." ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
            ) : (
              <button
                key={item}
                onClick={() => setPage(item)}
                className={`px-4 py-2 rounded-md border text-sm transition shadow-sm font-semibold ${
                  item === page
                    ? "bg-[var(--secondary-500)] text-white border-[var(--secondary-500)]"
                    : "border-gray-300 hover:bg-gray-100"
                }`}
              >
                {item}
              </button>
            )
          )}
        <button
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-4 py-2 rounded-md border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-100 transition shadow-sm font-semibold"
        >
          {t("common.next")}
        </button>
      </div>
    );
  };

  return (
    <div className="favourites-page py-10 min-h-screen">
      <div className="container">
        <section className="py-8">
          {/* Tabs Navigation */}
          <div className="flex items-center justify-center border-b border-gray-200 mb-8">
            <div className="flex gap-4 sm:gap-8">
              <button
                type="button"
                className="px-6 py-3 text-base font-semibold whitespace-nowrap border-b-2 border-[var(--secondary-500)] text-[var(--secondary-500)] transition-all duration-300 cursor-default"
              >
                {t("favourites.sellers", { defaultValue: "Advertisers" })}
                {sellers.length > 0 && (
                  <span className="ml-2 text-xs py-0.5 px-2 rounded-full bg-pink-100 text-pink-700">
                    {sellers.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {sellers.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200 shadow-sm mx-4">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                </svg>
              </div>
              <p className="text-gray-500 font-bold text-lg">
                {t("favourites.no_sellers", { defaultValue: "No favourite Advertiser found" })}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {t("favourites.explore_sellers", { defaultValue: "Explore more sellers and favorite them to see them here!" })}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 px-4">
                {currentItems.map((item, index) => (
                  <div 
                    key={item._id || index} 
                    className="flex flex-col cursor-pointer transition-transform hover:scale-[1.02]" 
                    onClick={() => navigate(getSellerProfileUrl(item))}
                  >
                    <SellerCard
                      images={item.profile_image}
                      title={item.name}
                      subtitle={item.store_name}
                      time={item.time}
                      live={item.live}
                    />
                  </div>
                ))}
              </div>
              {renderPagination()}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default Favourites;
